import React from "react";
import { useTranslate } from "./useTranslate";
import { useNavigate } from "react-router-dom";
import { ProjectT } from "../../models/models";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ActionDataT } from "../../models/models";
import { getAvailableActions } from "../helpers/getAvailableForProjectActions";
import {
  useCancelProjectMutation,
  useChangeProjectStatusMutation,
  useCloseProjectMutation,
  useDeleteProjectMutation,
  useLazyGetProjectByIdQuery,
  useRejectProjectMutation,
  useSendToAdAgencyMutation,
  useShareDeliveryMutation,
} from "../../api/projectsApi";
import usePopup from "./usePopup";
import { toggleLoading } from "../../store/slices/appSlice";
import { toast } from "react-toastify";
import { TOAST_DURATION } from "../helpers/constants";
import Button, { ButtonModeE } from "../../components/Button/Button";
import { H1, P, Span } from "../../components/Text/Text";
import { useWizardCreator } from "../../popups/Wizard/useWizardCreator";
import { WizardModeE, WizardTypeE } from "../../popups/Wizard/models";
import SendForCustomerApproval from "../../popups/SendForCustomerApproval";
import getVersionsErrors from "../helpers/getVersionsErrors";
import SendToBroadcasters from "../../popups/SendToBroadcasters";
import { AccessLevelE, ClientTypeE, StatusE } from "../../models/enums";
import Form from "../../components/Form/Form";
import { hasVideoGrantedExtraFrames } from "../helpers/hasVideoGrantedExtraFrames";
import { isDeliveryOrderPaid } from "../helpers/isDeliveryOrderPaid";
import Icon from "../../components/Icon/Icon";
import { RoutesE } from "../../routes/routesData";
import ChangeInternalProjectNumber from "../../popups/ChangeInternalProjectNumber";
import hasMXF from "../../pages/projects/helpers/hasMXF";
import ReDeliveryBuilder from "../../popups/ReDelivery/ReDeliveryBuilder";
import reDeliveryStyles from "../../popups/ReDelivery/reDelivery.module.scss";

import styles from "../styles/popup.module.scss";

const useDeliveryActions = (
  delivery: ProjectT,
  refetch: () => void
): {
  defaultActions: ActionDataT[];
  hasAvailableActions: boolean;
  closeDelivery?: () => void;
  sendForCustomerApproval?: () => void;
  sendToBroadcasters?: (campaignId: number) => void;
  saveAndContinue?: (setTermsRead: (value: boolean) => void) => void;
  sendDelivery?: () => void;
  shareDelivery?: () => void;
  handleChangeInternalNumber?: (internalNumber: string) => void;
  reBroadcastDelivery?: () => void;
} => {
  const user = useAppSelector(selectCurrentUser);
  const navigate = useNavigate();
  const { createPopup, removePopup } = usePopup();
  const createWizard = useWizardCreator();
  const dispatch = useAppDispatch();
  const { t } = useTranslate();

  const [deleteProject] = useDeleteProjectMutation();
  const [cancelProjectMutation] = useCancelProjectMutation();
  const [rejectProjectMutation] = useRejectProjectMutation();
  const [closeProjectMutation] = useCloseProjectMutation();
  const [changeProjectStatus] = useChangeProjectStatusMutation();
  const [sendToAdAgency] = useSendToAdAgencyMutation();
  const [getDeliveryData] = useLazyGetProjectByIdQuery();
  const [shareDelivery] = useShareDeliveryMutation();

  const navigateToProjectViewPage = (e) => {
    e?.preventDefault();
    navigate(`/deliveries/${delivery.Id}`);
  };

  const handleEditDelivery = () => {
    const wizardPopup = createWizard({
      campaignDeliveryId: delivery.Id,
      deliveryId: delivery.Id,
      mode: WizardModeE.Edit,
      type: WizardTypeE.Delivery,
    });
    createPopup(wizardPopup);
  };

  const handleDeleteProject = () => {
    const handleDelete = async () => {
      await deleteProject({ Id: delivery.Id });
      toast(t`Successfully deleted`, {
        type: "success",
        autoClose: TOAST_DURATION,
      });
      removePopup();
      navigate(RoutesE.CAMPAIGNS);
    };

    createPopup({
      header: <H1>Delete campaign</H1>,
      body: <P>Do you really want to delete campaign ? </P>,
      footer: (
        <div className={styles.cancelProjectFooterButtonsWrapper}>
          <Button onClick={removePopup} mode={ButtonModeE.OUTLINED}>
            Cancel
          </Button>
          <Button onClick={handleDelete} useGlobalLoading>
            Delete
          </Button>
        </div>
      ),
      className: styles.deleteProjectPopupWrapper,
    });
  };

  const handleConfirmCancelProject = () => {
    const handleCancel = async () => {
      try {
        const res = await cancelProjectMutation({ Id: delivery.Id });
        dispatch(toggleLoading());
        refetch();
        dispatch(toggleLoading());
        removePopup();
        toast(t`Successfully canceled`, {
          type: "success",
          autoClose: TOAST_DURATION,
        });
      } catch (e: any) {
        console.log(e);
      }
    };
    createPopup({
      header: <H1>Cancel project</H1>,
      body: (
        <P>
          Do you wish to cancel the project? All files for the project will be
          deleted. You can also cancel the project and create a new copy of it.
        </P>
      ),
      footer: (
        <div className={styles.cancelProjectFooterButtonsWrapper}>
          <Button mode={ButtonModeE.OUTLINED} onClick={removePopup}>
            Close
          </Button>
          <Button onClick={handleCancel} useGlobalLoading>
            Cancel project
          </Button>
        </div>
      ),
      className: styles.cancelProjectPopupWrapper,
    });
  };

  const handleRejectProject = () => {
    const handleReject = async () => {
      try {
        const res = await rejectProjectMutation({ Id: delivery.Id });
        removePopup();
        toast(t`Successfully rejected`, {
          type: "success",
        });
      } catch (e: any) {
        console.log(e);
      }
    };
    createPopup({
      header: <H1>Reject this Delivery</H1>,
      body: (
        <>
          <P>Do you wish to reject this delivery from broadcasting?</P>
          <P>
            Once rejected, the broadcasters will not be able to download this
            delivery for broadcasting.
          </P>
        </>
      ),
      footer: (
        <div className={styles.cancelProjectFooterButtonsWrapper}>
          <Button mode={ButtonModeE.OUTLINED} onClick={removePopup}>
            Close
          </Button>
          <Button onClick={handleReject} useGlobalLoading>
            Reject this delivery
          </Button>
        </div>
      ),
      className: styles.cancelProjectPopupWrapper,
    });
  };

  const handleCloneDelivery = () => {
    const wizardPopup = createWizard({
      campaignDeliveryId: delivery.Id,
      deliveryId: delivery.Id,
      mode: WizardModeE.Clone,
      type: WizardTypeE.Delivery,
    });
    createPopup(wizardPopup);
  };

  const handleConfirmCloseProject = () => {
    const handleClose = async () => {
      try {
        const res = await closeProjectMutation({ Id: delivery.Id });
        dispatch(toggleLoading());
        refetch();
        dispatch(toggleLoading());
        removePopup();
        toast("Successfully closed", {
          type: "success",
        });
      } catch (e: any) {
        console.log(e);
      }
    };

    createPopup({
      header: <H1>Close project</H1>,
      body: <P>Do you wish to close the project "{delivery.Name}"?</P>,
      footer: (
        <div className={styles.cancelProjectFooterButtonsWrapper}>
          <Button mode={ButtonModeE.OUTLINED} onClick={removePopup}>
            Cancel
          </Button>
          <Button onClick={handleClose} useGlobalLoading>
            Close project
          </Button>
        </div>
      ),
      className: styles.cancelProjectPopupWrapper,
    });
  };

  const handleSendForCustomerApproval = async () => {
    createPopup(SendForCustomerApproval(removePopup, delivery.Id));
  };

  const sendToBroadcasters = async (campaignId: number) => {
    let deliveryForSending = delivery;

    if (!delivery?.Versions) {
      // fetch more data about the delivery if it is need
      const { data } = await getDeliveryData({
        id: delivery.Id,
        detailed: true,
      });
      deliveryForSending = data;
    }

    const errors = getVersionsErrors(deliveryForSending.Versions);

    // spec-26 Phase 2 — read BOTH halves of this from `deliveryForSending`, not `delivery`.
    // `delivery` may be the list-shaped object that was just replaced by the detailed fetch above,
    // and on a list shape `CreatedByDeliveryServicePrivate` is undefined — so the whole guard
    // collapsed to `true` and waved the send through without ever looking at the order status.
    // Latent rather than live today (the property is genuinely absent from the list view model), but
    // it becomes reachable the moment anyone adds it there, and it fails OPEN, which is the wrong
    // direction for a guard standing between an unpaid order and the broadcasters.
    const isBilled = deliveryForSending?.CreatedByDeliveryServicePrivate
      ? isDeliveryOrderPaid(
          deliveryForSending?.DeliveryServiceDetailsViewModel?.OrderStatus
        )
      : true;

    try {
      if (!isBilled) {
        createPopup({
          header: <H1>The campaign cannot be delivered to the broadcasters</H1>,
          body: (
            <div className={styles.deliveryNote}>
              <div className={styles.description}>
                <P>
                  The sending of the campaign cannot be completed because the
                  payment process for the delivery service has not yet been
                  completed.
                </P>
                <P>
                  Please log back in with your delivery service user and
                  complete the payment process.
                </P>
              </div>
            </div>
          ),
          footer: <Button onClick={removePopup}>Close</Button>,
          className: styles.sendToBroadcastersDeniedPopup,
        });
      } else {
        createPopup(
          SendToBroadcasters(
            deliveryForSending?.Detailed.Broadcasters,
            campaignId,
            deliveryForSending?.Number,
            deliveryForSending.Campaign.Id,
            removePopup,
            refetch,
            errors
          )
        );
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  const handleDeliverySaveAndContinue = (
    setTermsRead: (value: boolean) => void
  ) => {
    const handleConfirmSaveAndContinue = async () => {
      // spec-26 Phase 2 (D-C's second hole) — never push an already-PAID order back through
      // changeProjectStatus. `ProjectsController.UserChangeStatus` writes OrderStatus = Pending on
      // every non-Cancelled transition, and that write is exactly what erased orders 8388/8393/8395.
      // Phase 1's PaidOrderGuard now refuses it in the BL, so this is no longer the only thing
      // standing between a paid order and a downgrade — but a refusal is LOUD by design (D8: an
      // Error log plus an operator alert mail), and raising a payment-incident alert every time
      // someone re-opens the order page would bury the real ones. Don't reach the guard at all.
      // The navigation still happens either way: the customer sees the same screen.
      if (!isDeliveryOrderPaid(delivery?.DeliveryServiceDetailsViewModel?.OrderStatus)) {
        await changeProjectStatus({
          id: delivery.Id,
        });
      }
      removePopup();
      navigate(`/projects/order-payment/${delivery.Id}`);
    };

    const handleCheckConfirmation = (value: boolean) => {
      setTermsRead(value);
    };

    const handleFormClick = (e) => {
      if (e.target.id === "terms-link") {
        createPopup({
          header: <H1>Terms</H1>,
          body: <p></p>,
        });
      }
    };

    setTimeout(() => {
      if (user.clientType === ClientTypeE["Delivery service"]) {
        const approveButton: HTMLButtonElement = document.getElementById(
          "send-delivery-button"
        ) as HTMLButtonElement;

        if (approveButton) {
          approveButton.disabled = true;
          approveButton.style.pointerEvents = "none";
        }
      }
    }, 0);

    const errors = getVersionsErrors(delivery.Versions);

    const hasVersionsGrantedExtraFrames = delivery.Versions.some((v) =>
      hasVideoGrantedExtraFrames(v.ActualFrameCount, v.RequiredLength)
    );

    createPopup({
      header: <H1>Do you wish to submit the project?</H1>,
      body: (
        <>
          {user.clientType === ClientTypeE["Delivery service"] && (
            <>
              <div className={styles.deliveryNote}>
                <Span className={styles.note}>Please note:&nbsp;</Span>
                <br />
                <Span className={styles.description}>
                  A delivery that is disqualified by the broadcaster due to
                  non-compliance with the technical requirements of the files,
                  will be considered as a complete delivery and will be charged
                  accordingly. Before approving the project, please wait until
                  the upload process is complete and make sure all versions are
                  available for preview. It is important to make sure that there
                  are no extra frames or resolution issues in any of the
                  versions. In case an error or warning is displayed for a
                  particular version, you can delete it and upload a revised
                  version again.
                </Span>
              </div>
              {errors?.length > 0 ? (
                <p className={styles.error}>
                  <Icon name="exclamation" className={styles.errorIcon} />
                  <Span>
                    Attention: some versions have actual lengths that differ
                    from their required lengths.
                  </Span>
                </p>
              ) : (
                <>
                  <P className={styles.sendProjectText}>
                    After submitting the project, you will be transferred to the
                    payment details page in order to complete the process of
                    sending it to the broadcasters
                  </P>
                  <div>
                    <Form
                      fields={[
                        {
                          type: "checkbox",
                          label:
                            "I confirm that I read and agreed to the terms of the JUMBOmedia delivery service.",
                          onChange: (v) => handleCheckConfirmation(v),
                        },
                      ]}
                      onSubmit={() => {}}
                      formID="no-submitted-form"
                      columns={1}
                      // className={styles.termsForm}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </>
      ),
      footer: (
        <div className={styles.cancelProjectFooterButtonsWrapper}>
          <span></span>
          {errors?.length === 0 && (
            <Button
              onClick={handleConfirmSaveAndContinue}
              useGlobalLoading
              id="send-delivery-button"
              // disabled={(user.clientType === 5) ? !termsRead : false}
            >
              Continue
            </Button>
          )}
        </div>
      ),
      className: styles.cancelProjectPopupWrapper,
    });
  };

  const handleConfirmSendProject = () => {
    const handleSend = async () => {
      removePopup();
      try {
        const res = await sendToAdAgency({ Id: delivery.Id });
        toast(t`Successfully sent`, {
          type: "success",
          autoClose: TOAST_DURATION,
        });
        // navigate(`/projects/order-payment/${data.Id}`);
      } catch (e: any) {
        console.log(e);
      }
    };

    const errors = getVersionsErrors(delivery.Versions);

    const hasVersionsGrantedExtraFrames = delivery.Versions.some((v) =>
      hasVideoGrantedExtraFrames(v.ActualFrameCount, v.RequiredLength)
    );

    const withMXF = hasMXF(delivery?.Versions);

    const NoRawMaterialsMessage = () =>
      !delivery?.AdaptationsRawMaterialsList && withMXF ? (
        <div className={styles.deliveryNote}>
          <Span className={styles.note}>Please note:&nbsp;</Span>
          <br />
          <Span className={styles.description}>
            This delivery has a version that is required for content adaptation.
            Before approval of the delivery please make sure that you uploaded
            all necessary raw materials for the content adaptation.
          </Span>
        </div>
      ) : (
        <></>
      );

    if (errors.length > 0) {
      createPopup({
        header: <H1>Send project</H1>,
        body: (
          <>
            <div className={styles.errorsWrapper}>
              {errors.some(
                (error) =>
                  error.incorrectLength ||
                  error.resolutionError ||
                  error.encodingError
              ) && (
                <p className={styles.error}>
                  <Icon name="exclamation" className={styles.errorIcon} />
                  <Span>
                    Attention: some versions have actual lengths that differ
                    from their required lengths.
                  </Span>
                </p>
              )}
              <NoRawMaterialsMessage />
              {/* {!errors.some(error => error.resolutionError) && (
                                <p className={styles.error}>
                                    <Icon name='exclamation' className={styles.errorIcon} />
                                    <Span>
                                        It is not possible to approve the delivery of these versions for broadcasting. Make sure that all versions are in accordance with the technical requirements of the instructions for closing the video files and do not have length problems or extra frames that do not match the version settings.
                                    </Span>
                                </p>
                            )} */}
            </div>
          </>
        ),
        footer: (
          <div className={styles.cancelProjectFooterButtonsWrapper}>
            <Button mode={ButtonModeE.OUTLINED} onClick={removePopup}>
              Back
            </Button>
          </div>
        ),
        className: styles.errorSendPopup,
      });
    } else {
      createPopup({
        header: <H1>Approve and send</H1>,
        body: (
          <div>
            {!hasVersionsGrantedExtraFrames && (
              <P className={styles.attentionExtraFrames}>
                Attention: One or more of the versions have extra frames that do
                not match exactly their required lengths. It is recommended to
                re-upload the version with the exact length.
              </P>
            )}
            <NoRawMaterialsMessage />
            <P className={styles.sendProjectText}>
              Do you wish to send the project to the advertising agency?
            </P>
          </div>
        ),
        footer: (
          <div className={styles.cancelProjectFooterButtonsWrapper}>
            <Button mode={ButtonModeE.OUTLINED} onClick={removePopup}>
              Cancel
            </Button>
            <Button onClick={handleSend} useGlobalLoading>
              Approve and send
            </Button>
          </div>
        ),
        className: styles.cancelProjectPopupWrapper,
      });
    }
  };

  const handleShare = async () => {
    // const body = {
    //     Id: delivery.Id,
    //     Email: '',
    //     ExtraText: 'test',
    //     ProjectType: "VideoAd",
    // };
    // await shareDelivery(body);
  };

  const handleSendToPostProduction = () => {
    const handleCLick = async () => {
      const res: any = await changeProjectStatus({
        id: delivery.Id,
        status: StatusE.Opened,
      });
      if (res.data) {
        toast(`Successfully sent`, {
          type: "success",
          autoClose: TOAST_DURATION,
        });
        removePopup();
      }
    };

    createPopup({
      header: <H1>Send the delivery</H1>,
      body: (
        <P>Do you want to send the delivery to the post production company?</P>
      ),
      footer: (
        <div className={styles.cancelProjectFooterButtonsWrapper}>
          <Button mode={ButtonModeE.OUTLINED} onClick={removePopup}>
            Cancel
          </Button>
          <Button onClick={handleCLick} useGlobalLoading>
            Send
          </Button>
        </div>
      ),
      className: styles.cancelProjectPopupWrapper,
    });
  };

  const handleChangeInternalNumber = async (internalNumber: string) => {
    createPopup(
      ChangeInternalProjectNumber(delivery.Id, removePopup, internalNumber)
    );
  };

  // Entry (b): re-broadcast a past delivery — opens the re-delivery builder pre-filled with this
  // delivery's number; it auto-creates a new campaign (editable date + name) on confirmed payment.
  const reBroadcastDelivery = () => {
    createPopup({
      body: (
        <ReDeliveryBuilder
          entryType="DeliveryFirst"
          prefilledNumber={delivery.Number}
        />
      ),
      className: reDeliveryStyles.popupWrapper,
    });
  };

  const actions: ActionDataT[] = user
    ? getAvailableActions(
        {
          StatusName: delivery?.StatusName,
        } as ProjectT,
        user,
        {
          download: navigateToProjectViewPage,
          upload: navigateToProjectViewPage,
          edit: handleEditDelivery,
          delete: handleDeleteProject,
          cancel: handleConfirmCancelProject,
          reject: handleRejectProject,
          settings: null,
          close: null,
          copy: handleCloneDelivery,
          sendToPost: handleSendToPostProduction,
          sendToBroadcast: sendToBroadcasters,
          sendToCustomerApproval: handleSendForCustomerApproval,
          reBroadcast: reBroadcastDelivery,
        }
      )
    : [];

  if (!delivery)
    return {
      defaultActions: [],
      hasAvailableActions: false,
    };

  return {
    defaultActions: actions,
    hasAvailableActions: !!actions.filter(
      ({ action, condition, accessFor }) =>
        action !== null &&
        condition &&
        (Array.isArray(accessFor)
          ? accessFor.some((role) => role === user.role)
          : accessFor === user.role)
    )?.length,
    closeDelivery: handleConfirmCloseProject,
    sendForCustomerApproval: handleSendForCustomerApproval,
    sendToBroadcasters,
    saveAndContinue: handleDeliverySaveAndContinue,
    sendDelivery: handleConfirmSendProject,
    shareDelivery: handleShare,
    handleChangeInternalNumber,
    reBroadcastDelivery,
  };
};

export default useDeliveryActions;
